/**
 * The three states every data screen can be in, as components.
 *
 * These exist because the alternative is what the document list had for months:
 * a blank region. A blank region reads as a broken app, and "is it loading, or
 * is there genuinely nothing?" is a question a user should never have to ask.
 *
 * All three are presentational and take the retry action as a prop. A component
 * that both fetched and rendered could not be told "show your error state" by
 * a screen, which is the only reason anyone can build one correctly.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../lib/cn';

export function LoadingState({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div
      // `role="status"` so a screen reader announces the wait rather than
      // reading an empty region and concluding the page is blank.
      role="status"
      aria-live="polite"
      data-testid="loading-state"
      className={cn('flex items-center gap-2 p-6 text-sm text-slate-400', className)}
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  /** What the user can do about it. An empty state with no action is a dead end. */
  readonly description?: string;
  readonly action?: ReactNode;
  readonly icon?: ReactNode;
  readonly className?: string;
}

export function EmptyState({ title, description, action, icon, className }: EmptyStateProps) {
  return (
    <div
      data-testid="empty-state"
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-800 p-10 text-center',
        className,
      )}
    >
      <span className="text-slate-600">{icon ?? <Inbox className="h-8 w-8" aria-hidden />}</span>
      <p className="text-sm font-medium text-slate-200">{title}</p>
      {description ? <p className="max-w-md text-xs text-slate-500">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  /** The reason, already phrased for a person. */
  readonly message: string;
  readonly title?: string;
  /** Omit when there is genuinely nothing to retry. */
  readonly onRetry?: () => void;
  readonly className?: string;
}

export function ErrorState({ message, title = 'Something went wrong', onRetry, className }: ErrorStateProps) {
  return (
    <div
      // `role="alert"`: an error that is only visible is an error the user misses.
      role="alert"
      data-testid="error-state"
      className={cn(
        'flex flex-col items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-4',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" aria-hidden />
        <div>
          <p className="text-sm font-medium text-red-100">{title}</p>
          <p className="mt-1 text-xs text-red-200/80">{message}</p>
        </div>
      </div>
      {onRetry ? (
        <Button size="sm" variant="secondary" icon={<RefreshCw className="h-3.5 w-3.5" aria-hidden />} onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/**
 * A destination the plan lists but has not built.
 *
 * This is the honest alternative to hiding the menu entry. It names what is
 * planned, so the menu can be complete without the app pretending — and it does
 * NOT offer a button that goes nowhere, because a dead button is worse than an
 * honest gap.
 */
export function NotImplementedState({
  title,
  group,
}: {
  readonly title: string;
  readonly group?: string;
}) {
  return (
    <div className="p-6">
      <EmptyState
        title={`${title} is not built yet`}
        description={
          group
            ? `This screen is part of the ${group} area in the plan. Nothing is shown here yet, because a page that looked finished and was not would be worse than an honest gap.`
            : 'This screen is in the plan. Nothing is shown here yet, because a page that looked finished and was not would be worse than an honest gap.'
        }
      />
    </div>
  );
}
